import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, FileAudio, AlertCircle, CheckCircle, Clock, Download } from 'lucide-react';
import { transcriptionAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';

interface UploadJob {
  id: string;
  fileName: string;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  progress: number;
  audioLength?: number;
  transcriptionId?: string;
  createdAt: Date;
}

type SerializableJob = Omit<UploadJob, 'createdAt'> & { createdAt: string };

/**
 * UploadPage — быстрый вариант с удалением завершённых задач из localStorage,
 * но без удаления их непосредственно со страницы (UI остаётся до навигации).
 *
 * Ключевые изменения:
 * - В storage храним только активные задачи (queued | processing).
 * - При переходе задачи в done|failed — удаляем её из storage (чтобы при возврате на страницу
 *   она не восстанавливалась). На UI задача остаётся, пока пользователь не уйдёт/не перезагрузит страницу.
 * - Корректно управляем polling-интервалами (чтобы не было утечек).
 */

export const UploadPage: React.FC = () => {
  const { user } = useAuth();
  const [jobs, setJobs] = useState<UploadJob[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);

  // Мониторы для polling статусов (interval ids)
  const monitorsRef = useRef<Record<string, number>>({});

  // --- localStorage helpers ---
  // Сохраняем ТОЛЬКО активные задачи (queued | processing)
  const saveActiveJobsToStorage = (allJobs: UploadJob[]) => {
    try {
      const active = allJobs.filter(j => j.status === 'queued' || j.status === 'processing');
      const serializable: SerializableJob[] = active.map(j => ({
        ...j,
        createdAt: j.createdAt.toISOString(),
      }));
      localStorage.setItem('upload_jobs', JSON.stringify(serializable));
    } catch (e) {
      console.warn('Не удалось сохранить активные jobs в localStorage', e);
    }
  };

  const loadActiveJobsFromStorage = (): UploadJob[] => {
    try {
      const raw = localStorage.getItem('upload_jobs');
      if (!raw) return [];
      const parsed: SerializableJob[] = JSON.parse(raw);
      // возвращаем только queued|processing (хотя storage уже хранит только их)
      return parsed
        .map(p => ({ ...p, createdAt: new Date(p.createdAt) }))
        .filter(p => p.status === 'queued' || p.status === 'processing');
    } catch (e) {
      console.warn('Не удалось прочитать jobs из localStorage', e);
      return [];
    }
  };

  // Удаляем конкретную задачу из storage (не трогаем state)
  const removeJobFromStorage = (jobId: string) => {
    try {
      const raw = localStorage.getItem('upload_jobs');
      if (!raw) return;
      const parsed: SerializableJob[] = JSON.parse(raw);
      const filtered = parsed.filter(p => p.id !== jobId);
      localStorage.setItem('upload_jobs', JSON.stringify(filtered));
    } catch (e) {
      console.warn('Не удалось удалить job из localStorage', e);
    }
  };

  // --- Инициализация: загружаем активные задачи и рестартим мониторинг ---
  useEffect(() => {
    const persistedActive = loadActiveJobsFromStorage();
    if (persistedActive.length) {
      setJobs(persistedActive);
      persistedActive.forEach(j => {
        // рестартим мониторинг только для активных задач
        monitorJobStatus(j.id);
      });
    }

    // Очистка интервалов при размонтировании страницы/компонента
    return () => {
      Object.values(monitorsRef.current).forEach(id => {
        try { clearInterval(id); } catch {}
      });
      monitorsRef.current = {};
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Сохраняем в storage при изменении списка задач — но сохраняем ТОЛЬКО активные
  useEffect(() => {
    saveActiveJobsToStorage(jobs);
  }, [jobs]);

  // --- Polling: мониторинг статусов ---
  const monitorJobStatus = (jobId: string) => {
    // если уже мониторится — не делаем повторно
    if (monitorsRef.current[jobId]) return;

    let stopped = false;

    const checkStatus = async () => {
      if (stopped) return;
      try {
        console.log('Проверяем статус для job_id:', jobId);
        const status = await transcriptionAPI.getStatus(jobId);
        console.log('Статус задачи:', status);

        // обновляем UI-стейт (эта запись останется на странице до навигации/перезагрузки)
        setJobs(prev => {
          const next = prev.map(job => {
            if (job.id === jobId) {
              const updated: UploadJob = { ...job };
              switch (status.status) {
                case 'queued':
                  updated.status = 'queued';
                  updated.progress = 10;
                  break;
                case 'processing':
                  updated.status = 'processing';
                  updated.progress = 50;
                  break;
                case 'done':
                  updated.status = 'completed';
                  updated.progress = 100;
                  updated.transcriptionId = status.output?.transcription_id;
                  toast.success(`Стенограмма для "${job.fileName}" готов!`);
                  try {
                    const detail = { transcriptionId: status.output?.transcription_id, jobId };
                    window.dispatchEvent(new CustomEvent('transcription:ready', { detail }));
                  } catch (e) {
                    console.warn('Не удалось отправить событие transcription:ready', e);
                  }
                  break;
                case 'failed':
                  updated.status = 'failed';
                  updated.progress = 0;
                  toast.error(`Ошибка при создании стенограммы для "${job.fileName}"`);
                  break;
                default:
                  console.log('Неизвестный статус:', status.status);
              }
              return updated;
            }
            return job;
          });
          return next;
        });

        // Если задача завершилась — остановим polling и удалим её из storage
        if (status.status === 'done' || status.status === 'failed') {
          const intervalId = monitorsRef.current[jobId];
          if (intervalId) {
            clearInterval(intervalId);
            delete monitorsRef.current[jobId];
          }
          stopped = true;

          // Удаляем из localStorage — UI останется до навигации
          try {
            removeJobFromStorage(jobId);
          } catch (e) {
            console.warn('Ошибка при удалении job из storage после завершения', e);
          }
        }
      } catch (error) {
        console.error('Ошибка при проверке статуса:', error);
        // не останавливаем — повторим позже
      }
    };

    // Немедленная проверка, затем интервал
    checkStatus();
    const id = window.setInterval(checkStatus, 10000);
    monitorsRef.current[jobId] = id;
  };

  // --- Upload handler ---
  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return;
      const file = acceptedFiles[0];

      try {
        if (!user?.user_id && !user?.email) {
          toast.error('Пользователь не авторизован');
          return;
        }
        const chatId = user.user_id || user.email;

        setIsUploading(true);
        setUploadProgress(0);

        const response = await transcriptionAPI.upload(
          file,
          chatId,
          undefined,
          true,
          (percent: number) => {
            setUploadProgress(percent);
          }
        );

        if (!response.job_id) {
          throw new Error('API не вернул job_id');
        }

        const newJob: UploadJob = {
          id: response.job_id,
          fileName: file.name,
          status: 'queued',
          progress: 10,
          audioLength: response.audio_length,
          createdAt: new Date(),
        };

        // Добавляем задачу в UI. saveActiveJobsToStorage в useEffect сохранит её в storage,
        // потому что статус queued -> активный.
        setJobs(prev => [newJob, ...prev]);

        toast.success('Файл загружен! Начинается обработка...');
        setUploadProgress(null);
        setIsUploading(false);

        // Запускаем мониторинг
        monitorJobStatus(response.job_id);
      } catch (error: any) {
        console.error('Ошибка при загрузке файла:', error);
        toast.error(error?.message || 'Ошибка при загрузке файла');
        setUploadProgress(null);
        setIsUploading(false);
      }
    },
    [user]
  );

  // --- Скачивание стенограммы ---
  const downloadTranscription = async (transcriptionId: string, fileName: string) => {
    try {
      const blob = await transcriptionAPI.download(transcriptionId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${fileName}_transcription.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Стенограмма скачана!');
    } catch (error) {
      console.error('Ошибка при скачивании стенограммы', error);
      toast.error('Ошибка при скачивании стенограммы');
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'audio/*': ['.mp3', '.wav', '.m4a', '.ogg', '.flac', '.aac'],
      'video/*': ['.mp4', '.webm', '.mkv', '.wmv', '.mov', '.avi'],
      'text/*': ['.docx', '.doc', '.pdf', '.txt', '.json'],
    },
    maxFiles: 1,
    disabled: isUploading,
  });

  // --- UI helpers ---
  const getStatusIcon = (status: UploadJob['status']) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'failed':
        return <AlertCircle className="h-5 w-5 text-red-500" />;
      case 'processing':
        return <Clock className="h-5 w-5 text-blue-500 animate-pulse" />;
      default:
        return <Clock className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusText = (status: UploadJob['status']) => {
    switch (status) {
      case 'queued':
        return 'В очереди';
      case 'processing':
        return 'Обработка';
      case 'completed':
        return 'Завершено';
      case 'failed':
        return 'Ошибка';
      default:
        return 'Неизвестно';
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-secondary-900">Загрузка файлов</h1>
        <p className="mt-2 text-secondary-600">
          Загрузите файл для извлечения стенограммы встречи
        </p>
      </div>

      <div className="card">
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
            isDragActive
              ? 'border-primary-400 bg-primary-50'
              : 'border-secondary-300 hover:border-primary-400 hover:bg-primary-50'
          } ${isUploading ? 'opacity-80 cursor-not-allowed' : 'cursor-pointer'}`}
        >
          <input {...getInputProps()} />
          <div className="flex flex-col items-center">
            <Upload className="h-12 w-12 text-secondary-400 mb-4" />

            {/* --- Здесь: отображаем прогресс загрузки в Dropzone вместо spinner --- */}
            {isUploading && uploadProgress !== null ? (
              <div className="w-full max-w-md space-y-2">
                <p className="text-sm text-secondary-600">Загрузка файла: {uploadProgress}%</p>
                <div className="w-full bg-secondary-200 rounded-full h-2">
                  <div
                    className="bg-primary-600 h-2 rounded-full transition-all duration-150"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
              </div>
            ) : isUploading ? (
              // fallback — если isUploading true, но прогресс неизвестен
              <div className="space-y-2">
                <div className="loading-spinner mx-auto"></div>
                <p className="text-sm text-secondary-600">Загрузка файла...</p>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-lg font-medium text-secondary-900">
                  {isDragActive ? 'Отпустите файл здесь' : 'Перетащите файл сюда'}
                </p>
                <p className="text-sm text-secondary-600">
                  или нажмите для выбора файла
                </p>
                <p className="text-xs text-secondary-500">
                  <span className="font-medium">Аудио: .mp3, .wav, .m4a, .ogg, .flac, .aac</span><br />
                  <span className="font-medium">Видео: .mp4, .webm, .mkv, .wmv, .mov, .avi</span><br />
                  <span className="font-medium">Стенограммы: .docx, .doc, .txt, .json</span>
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {jobs.length > 0 && (
        <div className="card">
          <h2 className="text-lg font-semibold text-secondary-900 mb-4">Обработка</h2>
          <div className="space-y-4">
            {jobs.map((job) => (
              <div key={job.id} className="border border-secondary-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <FileAudio className="h-5 w-5 text-secondary-400" />
                    <div>
                      <p className="text-sm font-medium text-secondary-900">{job.fileName}</p>
                      <p className="text-xs text-secondary-600">
                        {format(job.createdAt, 'dd MMMM yyyy, HH:mm', { locale: ru })}
                        {job.audioLength && ` • ${Math.round(job.audioLength / 60)} мин`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center space-x-2">
                      {getStatusIcon(job.status)}
                      <span className="text-sm text-secondary-600">
                        {getStatusText(job.status)}
                      </span>
                    </div>
                    {job.status === 'completed' && job.transcriptionId && (
                      <button
                        onClick={() => downloadTranscription(job.transcriptionId!, job.fileName)}
                        className="btn-secondary flex items-center space-x-1"
                      >
                        <Download className="h-4 w-4" />
                        <span>Скачать</span>
                      </button>
                    )}
                  </div>
                </div>

                {/* Прогресс-бар — показываем только для processing/queued (обработка на сервере) */}
                {(job.status === 'processing' || job.status === 'queued') && (
                  <div className="mt-3">
                    <div className="flex justify-between text-xs text-secondary-600 mb-1">
                      <span>Прогресс</span>
                      <span>{job.progress}%</span>
                    </div>
                    <div className="w-full bg-secondary-200 rounded-full h-2">
                      <div
                        className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${job.progress}%` }}
                      ></div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};